/**
 * InsightCard - KPI card with donut chart and breakdown list
 *
 * Atomic level: Molecule
 * Composes: DonutChart
 *
 * Displays a percentage metric as a donut chart with integrated breakdown list.
 * Used in the Conduct Overview panel.
 * Following GO UI Design System patterns.
 */

import React from 'react';
import { cn } from '../../../../lib/utils';
import { DonutChart } from './DonutChart';

export interface BreakdownItem {
  label: string;
  value: number;
}

export interface InsightCardProps {
  /** Card title */
  title: string;
  /** Main percentage value (0-100) */
  percentage: number;
  /** Accent color for the donut chart */
  color: string;
  /** Breakdown items displayed as a list */
  breakdowns?: BreakdownItem[];
  /** Additional CSS classes */
  className?: string;
}

const InsightCard = React.forwardRef<HTMLDivElement, InsightCardProps>(
  ({ title, percentage, color, breakdowns, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col gap-3 rounded-xl border border-border/60 bg-card px-4 py-4',
          className
        )}
      >
        {/* Title */}
        <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>

        {/* Donut + Breakdown row */}
        <div className="flex items-center gap-4">
          <DonutChart percentage={percentage} color={color} size={80} strokeWidth={8} />

          {/* Breakdown list */}
          {breakdowns && breakdowns.length > 0 && (
            <ul className="flex flex-col gap-1.5 text-xs">
              {breakdowns.map((item) => (
                <li key={item.label} className="flex items-center gap-2">
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-muted-foreground">
                    {item.label}: <span className="font-semibold text-foreground">{Math.round(item.value)}%</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }
);

InsightCard.displayName = 'InsightCard';

export { InsightCard };
